/* ==========================================================================
   AMADA 收藏（Favorites）数据层
   --------------------------------------------------------------------------
   职责：只管数据，不管 UI。四处消费方（导航心形徽标 / 手机版 Favorites 页 /
   PC 端 Favorites 抽屉 / 详情页各处心形）统一从这里读写并订阅同一个事件。

   存储：localStorage，key = amada:favorites:v1
     {
       v: 1,
       updatedAt: <ms>,
       items: [{ handle, variantId(可 null), qty, addedAt, snap:{title,image,price} }]
     }
   · snap 只用于「打开页面/抽屉时先渲染骨架」，防白屏闪烁；
     真实价格/库存一律以 /products/{handle}.js 拉回的为准，快照不当真相源
     （价格会变、商品会下架）。
   · 同一商品的不同变体算两条（稿里有 QUANTITY 步进器，语义接近购物车）。

   已知限制（V1 已与用户确认）：跨设备不同步；Safari 长期不活跃可能清除；
   隐私模式不可写（已 try/catch 降级为内存态，页面内仍可用）。
   预留 v 字段，将来可迁移到 customer metafields 做登录态同步。

   事件：写入后广播 document 上的 'amada:favorites:change'
         detail = { items, count }
         跨标签页监听 window 'storage'，重读后广播同一事件，消费方只订阅这一个。
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'amada:favorites:v1';
  var SCHEMA_VERSION = 1;
  var CHANGE_EVENT = 'amada:favorites:change';

  /* localStorage 可能整个不可用（Safari 隐私模式直接抛错）。
     探测一次，不可用就退化成「内存态」：本次会话内功能完整，刷新后丢失。
     绝不能让一个 setItem 抛错把整条交互链路打断。 */
  var storageOK = (function () {
    try {
      var k = '__amada_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch (e) {
      return false;
    }
  })();

  var memoryState = null; // storageOK=false 时的兜底容器

  function emptyState() {
    return { v: SCHEMA_VERSION, updatedAt: 0, items: [] };
  }

  /* ---------- 读写 ---------- */

  function read() {
    if (!storageOK) {
      return memoryState ? clone(memoryState) : emptyState();
    }
    var raw;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return emptyState();
    }
    if (!raw) return emptyState();

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return emptyState(); // 脏数据直接丢弃，不让它把后续逻辑带崩
    }
    if (!parsed || !Array.isArray(parsed.items)) return emptyState();

    // 逐条规整：字段缺失/类型不对的条目直接剔除，保证下游拿到的一定是干净结构
    var items = [];
    for (var i = 0; i < parsed.items.length; i++) {
      var it = normalize(parsed.items[i]);
      if (it) items.push(it);
    }
    return {
      v: parsed.v || SCHEMA_VERSION,
      updatedAt: parsed.updatedAt || 0,
      items: items
    };
  }

  function write(state) {
    state.v = SCHEMA_VERSION;
    state.updatedAt = Date.now();
    if (storageOK) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // 配额满或被策略拒绝：退回内存态，本次会话仍可用
        storageOK = false;
        memoryState = clone(state);
      }
    } else {
      memoryState = clone(state);
    }
    broadcast(state);
    return state;
  }

  function clone(o) {
    return JSON.parse(JSON.stringify(o));
  }

  /* ---------- 条目规整与匹配 ---------- */

  // 变体 ID 统一成字符串再比较：DOM data-* 取出来是字符串，
  // Shopify 接口给的是数字，不统一会出现「看着一样却匹配不上」。
  function vid(v) {
    return v === null || v === undefined || v === '' ? null : String(v);
  }

  function normalize(raw) {
    if (!raw || typeof raw.handle !== 'string' || !raw.handle) return null;
    var qty = parseInt(raw.qty, 10);
    if (!(qty > 0)) qty = 1;
    var snap = raw.snap && typeof raw.snap === 'object' ? raw.snap : {};
    return {
      handle: raw.handle,
      variantId: vid(raw.variantId),
      qty: qty,
      addedAt: raw.addedAt || Date.now(),
      snap: {
        title: snap.title || '',
        image: snap.image || '',
        price: typeof snap.price === 'number' ? snap.price : null,
        comparePrice: typeof snap.comparePrice === 'number' ? snap.comparePrice : null
      }
    };
  }

  function indexOf(items, handle, variantId) {
    var target = vid(variantId);
    for (var i = 0; i < items.length; i++) {
      if (items[i].handle === handle && items[i].variantId === target) return i;
    }
    return -1;
  }

  /* ---------- 事件广播 ---------- */

  var subscribers = [];

  function broadcast(state) {
    var detail = { items: state.items.slice(), count: state.items.length };
    for (var i = 0; i < subscribers.length; i++) {
      try {
        subscribers[i](detail);
      } catch (e) {
        /* 单个订阅者出错不能影响其它订阅者 */
      }
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: detail }));
  }

  /* 跨标签页：另一个标签写了 localStorage，本标签同步刷新。
     注意 storage 事件只在「其它标签」触发，本标签自己的写入不会回调。 */
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    broadcast(read());
  });

  /* ---------- 对外 API ---------- */

  var API = {
    STORAGE_KEY: STORAGE_KEY,
    CHANGE_EVENT: CHANGE_EVENT,

    /** 是否持久化可用（false = 内存态，刷新即丢） */
    isPersistent: function () {
      return storageOK;
    },

    /** 全部条目（副本，外部改了不影响内部状态） */
    list: function () {
      return read().items;
    },

    count: function () {
      return read().items.length;
    },

    has: function (handle, variantId) {
      return indexOf(read().items, handle, variantId) !== -1;
    },

    /** 该商品是否有任意变体被收藏（心形按 handle 点亮时用） */
    hasProduct: function (handle) {
      var items = read().items;
      for (var i = 0; i < items.length; i++) {
        if (items[i].handle === handle) return true;
      }
      return false;
    },

    /**
     * 加入收藏。已存在同 handle+variantId 则只更新快照，不重复添加、不叠加数量
     * （收藏不是购物车，重复点心形的预期是「保持已收藏」而不是「数量+1」）。
     * @returns {boolean} 当前是否处于已收藏状态（恒为 true）
     */
    add: function (item) {
      var next = normalize(item);
      if (!next) return false;
      var state = read();
      var i = indexOf(state.items, next.handle, next.variantId);
      if (i === -1) {
        state.items.push(next); // 新条目追加在末尾：稿里的顺序是「先收藏的在前」
      } else {
        state.items[i].snap = next.snap;
      }
      write(state);
      return true;
    },

    remove: function (handle, variantId) {
      var state = read();
      var i = indexOf(state.items, handle, variantId);
      if (i === -1) return false;
      state.items.splice(i, 1);
      write(state);
      return true;
    },

    /** 移除该 handle 下的全部变体（心形按 handle 取消时用） */
    removeProduct: function (handle) {
      var state = read();
      var before = state.items.length;
      state.items = state.items.filter(function (it) {
        return it.handle !== handle;
      });
      if (state.items.length === before) return false;
      write(state);
      return true;
    },

    /** @returns {boolean} 切换后是否为已收藏 */
    toggle: function (item) {
      var next = normalize(item);
      if (!next) return false;
      if (API.has(next.handle, next.variantId)) {
        API.remove(next.handle, next.variantId);
        return false;
      }
      API.add(next);
      return true;
    },

    setQty: function (handle, variantId, qty) {
      var n = parseInt(qty, 10);
      if (!(n > 0)) n = 1;
      if (n > 99) n = 99; // 与步进器上限一致，防手输/脚本写入异常值
      var state = read();
      var i = indexOf(state.items, handle, variantId);
      if (i === -1) return false;
      if (state.items[i].qty === n) return false; // 没变就不写、不广播
      state.items[i].qty = n;
      write(state);
      return true;
    },

    clear: function () {
      write(emptyState());
    },

    /**
     * 订阅变化。回调立即收到一次当前状态，省得每个消费方自己初始化一遍。
     * @returns {function} 取消订阅
     */
    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      subscribers.push(fn);
      var state = read();
      try {
        fn({ items: state.items.slice(), count: state.items.length });
      } catch (e) {
        /* 忽略首次回调里的异常 */
      }
      return function () {
        var i = subscribers.indexOf(fn);
        if (i !== -1) subscribers.splice(i, 1);
      };
    },

    /**
     * 用 /products/{handle}.js 刷新价格与库存。
     * 同源免鉴权、走 CDN 缓存，比 Storefront API 省掉一整套 token/GraphQL。
     * 单条失败不影响其它条（Promise.allSettled），拉不到的标 unavailable：
     * 商品下架/删除时必须能感知，否则批量加购会整单 422。
     *
     * @returns {Promise<Array>} 每项 = 原条目 + {product, variant, available, price,
     *          comparePrice, title, image, url, unavailable}
     */
    resolve: function (items) {
      var list = items || API.list();
      var handles = [];
      for (var i = 0; i < list.length; i++) {
        if (handles.indexOf(list[i].handle) === -1) handles.push(list[i].handle);
      }

      return Promise.allSettled(
        handles.map(function (h) {
          return fetch('/products/' + encodeURIComponent(h) + '.js', {
            headers: { Accept: 'application/json' }
          }).then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          });
        })
      ).then(function (results) {
        var byHandle = {};
        results.forEach(function (res, idx) {
          if (res.status === 'fulfilled' && res.value) byHandle[handles[idx]] = res.value;
        });

        return list.map(function (item) {
          var product = byHandle[item.handle];
          if (!product) {
            // 拉不到 = 已删除/已下架：保留快照让用户认得出是哪件，但禁止参与加购与合计
            return Object.assign({}, item, {
              product: null,
              variant: null,
              unavailable: true,
              available: false,
              title: item.snap.title,
              image: item.snap.image,
              price: item.snap.price,
              comparePrice: item.snap.comparePrice,
              url: '/products/' + item.handle
            });
          }

          var variant = null;
          if (item.variantId) {
            for (var j = 0; j < product.variants.length; j++) {
              if (String(product.variants[j].id) === item.variantId) {
                variant = product.variants[j];
                break;
              }
            }
          }
          // 没存变体（从列表卡收藏）或存的变体已被删：落到第一个可售变体
          if (!variant) {
            for (var k = 0; k < product.variants.length; k++) {
              if (product.variants[k].available) {
                variant = product.variants[k];
                break;
              }
            }
          }
          if (!variant) variant = product.variants[0] || null;

          var image =
            (variant && variant.featured_image && variant.featured_image.src) ||
            product.featured_image ||
            (product.images && product.images[0]) ||
            item.snap.image;

          return Object.assign({}, item, {
            product: product,
            variant: variant,
            unavailable: false,
            available: !!(variant && variant.available),
            title: product.title,
            image: image,
            price: variant ? variant.price : product.price,
            comparePrice: variant ? variant.compare_at_price : product.compare_at_price,
            url: '/products/' + product.handle + (variant ? '?variant=' + variant.id : '')
          });
        });
      });
    }
  };

  window.AmadaFavorites = API;

  /* ------------------------------------------------------------------
     心形统一交互：document 级事件委托
     一处监听管全站，包含 JS 后注入的元素（PERFECT PAIRINGS 卡片是动态生成的，
     用逐元素 addEventListener 必然漏）。
     DOM 契约：
       <button data-favorites-toggle
               data-product-handle="..."      必填
               data-variant-id="..."          可选
               data-product-title / -image / -price   可选，用于快照
     ------------------------------------------------------------------ */
  document.addEventListener(
    'click',
    function (e) {
      var el = e.target.closest && e.target.closest('[data-favorites-toggle]');
      if (!el) return;

      var handle = el.getAttribute('data-product-handle');
      if (!handle) return;

      // 心形常写成 <a href="/pages/favorites"> 以便 JS 失效时仍可达；
      // JS 生效时这里接管，不能让它跳走
      e.preventDefault();

      var priceAttr = parseInt(el.getAttribute('data-product-price'), 10);
      var compareAttr = parseInt(el.getAttribute('data-product-compare-price'), 10);

      var added = API.toggle({
        handle: handle,
        variantId: el.getAttribute('data-variant-id'),
        qty: 1,
        snap: {
          title: el.getAttribute('data-product-title') || '',
          image: el.getAttribute('data-product-image') || '',
          price: isNaN(priceAttr) ? null : priceAttr,
          comparePrice: isNaN(compareAttr) ? null : compareAttr
        }
      });

      el.setAttribute('aria-pressed', added ? 'true' : 'false');
    },
    false
  );

  /* 状态回刷：任何来源的变化（本页操作、其它标签页、程序调用）都走这里，
     把页面上所有心形和导航徽标刷成最新态。 */
  function paint(detail) {
    var toggles = document.querySelectorAll('[data-favorites-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      var el = toggles[i];
      var handle = el.getAttribute('data-product-handle');
      if (!handle) continue;
      var variantId = el.getAttribute('data-variant-id');
      // 卡片上的心形按 handle 点亮（用户不关心是哪个变体被收了）；
      // 详情页心形绑定当前选中变体，所以两种判定都要支持
      var on = variantId ? API.has(handle, variantId) : API.hasProduct(handle);
      el.classList.toggle('is-favorited', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    var badges = document.querySelectorAll('[data-favorites-count]');
    for (var j = 0; j < badges.length; j++) {
      badges[j].textContent = detail.count > 99 ? '99+' : String(detail.count);
      badges[j].hidden = detail.count === 0; // 0 件不显示徽标
    }
  }

  API.subscribe(paint);

  // DOM 还没解析完就跑了首次 paint（脚本可能在 head 里同步执行）时补一次
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      paint({ items: API.list(), count: API.count() });
    });
  }
})();
